import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { AgeVerification } from '../../components/auth/AgeVerification';
import { ParentalConsent } from '../../components/auth/ParentalConsent';
import { StatePrivacyNotice } from '../../components/auth/StatePrivacyNotice';
import { needsAdditionalConsent } from '../../utils/privacyCompliance';

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<'student' | 'parent' | 'coach'>('student');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Age verification states
  const [step, setStep] = useState<
    'register' | 'age-verify' | 'parental-consent' | 'state-privacy'
  >('register');
  const [birthDate, setBirthDate] = useState('');
  const [needsParentalConsent, setNeedsParentalConsent] = useState(false);
  const [parentEmail, setParentEmail] = useState('');
  const [userState, setUserState] = useState<string | undefined>();
  const [userAge, setUserAge] = useState<number>(0);

  const { signUp } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Move to age verification step
    setStep('age-verify');
  };

  const handleAgeVerified = (
    verifiedBirthDate: string,
    requiresConsent: boolean,
    state?: string
  ) => {
    setBirthDate(verifiedBirthDate);
    setNeedsParentalConsent(requiresConsent);
    setUserState(state);

    // Calculate age for state-specific requirements
    const age = new Date().getFullYear() - new Date(verifiedBirthDate).getFullYear();
    setUserAge(age);

    // Check if we need state-specific privacy notice
    if (state && needsAdditionalConsent(state, age)) {
      setStep('state-privacy');
    } else if (requiresConsent) {
      setStep('parental-consent');
    } else {
      // User is 18+, proceed with registration
      completeRegistration();
    }
  };

  const handleParentalConsent = (consentEmail: string) => {
    setParentEmail(consentEmail);
    completeRegistration();
  };

  const handleStatePrivacyAccept = () => {
    if (needsParentalConsent) {
      setStep('parental-consent');
    } else {
      completeRegistration();
    }
  };

  const handleOptOut = () => {
    // Store opt-out preference
    localStorage.setItem('dataOptOut', 'true');
    handleStatePrivacyAccept();
  };

  const completeRegistration = async () => {
    setLoading(true);
    setError('');

    try {
      // All values validated before passing

      // Ensure all required values are defined
      if (!email || !password) {
        setError('Email and password are required');
        setLoading(false);
        return;
      }

      // Pass additional metadata with the signup
      const { error } = await signUp(email, password, {
        data: {
          role,
          birth_date: birthDate,
          needs_parental_consent: needsParentalConsent,
          parent_email: parentEmail || null,
          age_verified_at: new Date().toISOString(),
          user_state: userState || null,
          opt_out_sale: localStorage.getItem('dataOptOut') === 'true',
          privacy_notice_accepted_at: new Date().toISOString(),
        },
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        // Go back to registration form on error
        setStep('register');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
      setStep('register');
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-rowing-blue rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-rowing-gray-900 mb-4">Check your email!</h2>
          <p className="text-rowing-gray-600 mb-6">
            We've sent a confirmation link to {email}. Click the link to activate your account.
          </p>
          <Link to="/login" className="text-rowing-blue hover:underline">
            Back to login
          </Link>
        </motion.div>
      </div>
    );
  }

  // Handle age verification step
  if (step === 'age-verify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AgeVerification onVerified={handleAgeVerified} onCancel={() => setStep('register')} />
        </motion.div>
      </div>
    );
  }

  // Handle parental consent step
  if (step === 'parental-consent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ParentalConsent
            userEmail={email}
            onConsent={handleParentalConsent}
            onCancel={() => setStep('register')}
          />
        </motion.div>
      </div>
    );
  }

  // Handle state privacy notice step
  if (step === 'state-privacy') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <StatePrivacyNotice
            state={userState}
            age={userAge}
            onAccept={handleStatePrivacyAccept}
            onOptOut={handleOptOut}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rowing-gray-50 to-white flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-display font-bold text-rowing-navy">GLYYD</h1>
            </Link>
            <h2 className="mt-4 text-2xl font-semibold text-rowing-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-rowing-gray-600">Start your rowing recruitment journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

            <div>
              <label className="block text-sm font-medium text-rowing-gray-700 mb-2">
                I am a...
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['student', 'parent', 'coach'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`py-2 px-4 rounded-lg border-2 font-medium transition-colors ${
                      role === r
                        ? 'border-rowing-blue bg-rowing-blue text-white'
                        : 'border-rowing-gray-300 text-rowing-gray-700 hover:border-rowing-gray-400'
                    }`}
                  >
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-rowing-gray-700 mb-2"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-rowing-gray-700 mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
                placeholder="••••••••"
              />
              <p className="mt-1 text-xs text-rowing-gray-500">Must be at least 8 characters</p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-rowing-gray-700 mb-2"
              >
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-rowing-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-rowing-blue hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-rowing-gray-600 hover:text-rowing-blue">
              ← Back to Home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
